import { Plus, Save } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getExecutionAudit, getExecutions, getPlans, saveExecution } from "../../data/api";
import { allocationPaletteSwatches, defaultAllocationLabelColor } from "../../data/allocationColors";
import type { AllocationRecord, ExecutionAllocationRecord, ExecutionRecord, RulePlanBundle } from "../../types";
import { money, number, pct, weightedAverageIndex } from "../../utils/calculations";

interface ExecutionAuditView {
  id: string;
  action: string;
  plan_id?: string;
  planId?: string;
  step_id?: string;
  stepId?: string;
  note?: string;
  created_at?: string;
  createdAt?: string;
}

interface ExecutionAllocationEditor {
  mode: "add" | "edit";
  stepId: string;
  itemId?: string;
  assetTicker: string;
  assetName: string;
  labelColor: string;
  amountText: string;
}

function executionAllocationId(stepId: string, ticker: string) {
  return `${stepId}-${ticker}-${Date.now()}-${Math.floor(Math.random() * 10000)}`.replace(/[^a-zA-Z0-9_-]/g, "-");
}

function editableAmount(value: string) {
  if (!value.trim()) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

export default function Execution() {
  const [plans, setPlans] = useState<RulePlanBundle[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [records, setRecords] = useState<ExecutionRecord[]>([]);
  const [audits, setAudits] = useState<ExecutionAuditView[]>([]);
  const [editor, setEditor] = useState<ExecutionAllocationEditor | null>(null);
  const [status, setStatus] = useState("Loading execution ledger...");
  const dirtyStepIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let mounted = true;
    getPlans().then((data) => {
      if (!mounted) return;
      setPlans(data);
      const initialPlan = data.find((plan) => plan.status === "active") ?? data[0];
      setSelectedPlanId(initialPlan?.id ?? "");
      if (!initialPlan) setStatus("No saved plan is available for execution");
    }).catch(() => {
      if (mounted) setStatus("Plan library could not be loaded");
    });
    return () => {
      mounted = false;
    };
  }, []);

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);

  useEffect(() => {
    if (!selectedPlanId) return;
    let mounted = true;
    setStatus("Loading selected plan execution...");
    Promise.all([getExecutions(selectedPlanId), getExecutionAudit(selectedPlanId).catch(() => [])]).then(([data, auditData]) => {
      if (!mounted) return;
      dirtyStepIdsRef.current.clear();
      setRecords(data);
      setAudits(auditData as ExecutionAuditView[]);
      setStatus("Manual execution ledger ready");
    }).catch(() => {
      if (mounted) setStatus("Execution ledger could not be loaded");
    });
    return () => {
      mounted = false;
    };
  }, [selectedPlanId]);

  const currency = selectedPlan?.budgetCurrency ?? "USD";
  const plannedTotal = records.reduce((sum, record) => sum + record.plannedAmount, 0);
  const executedTotal = records.reduce((sum, record) => sum + record.executedAmount, 0);
  const budgetUsedPct = plannedTotal > 0 ? (executedTotal / plannedTotal) * 100 : 0;
  const plannedWai = weightedAverageIndex(records.map((record) => ({ level: record.plannedLevel, amount: record.plannedAmount })));
  const executedWai = weightedAverageIndex(
    records
      .filter((record) => record.executedAmount > 0 && record.actualLevel)
      .map((record) => ({ level: record.actualLevel ?? 0, amount: record.executedAmount }))
  );

  const plannedAllocationsByStep = useMemo(() => {
    const byStep = new Map<string, AllocationRecord[]>();
    for (const allocation of selectedPlan?.allocations ?? []) {
      byStep.set(allocation.stepId, [...(byStep.get(allocation.stepId) ?? []), allocation]);
    }
    return byStep;
  }, [selectedPlan?.allocations]);

  const updateRecord = (stepId: string, patch: Partial<ExecutionRecord>) => {
    dirtyStepIdsRef.current.add(stepId);
    setRecords((current) => current.map((record) => (record.stepId === stepId ? { ...record, ...patch } : record)));
  };

  const normalizedExecutionRecord = (record: ExecutionRecord): ExecutionRecord => {
    const hasExecution = record.executedAmount > 0;
    return {
      ...record,
      status: record.status === "skipped" ? "skipped" : hasExecution ? "executed" : "pending",
      executedAt: hasExecution ? record.executedAt ?? new Date().toISOString() : undefined,
      plannedAllocations: record.plannedAllocations ?? plannedAllocationsByStep.get(record.stepId) ?? [],
      actualAllocations: record.actualAllocations ?? []
    };
  };

  useEffect(() => {
    if (!dirtyStepIdsRef.current.size) return;
    const timer = window.setTimeout(async () => {
      const dirtyIds = new Set(dirtyStepIdsRef.current);
      dirtyStepIdsRef.current.clear();
      const changed = records.filter((record) => dirtyIds.has(record.stepId));
      if (!changed.length) return;

      setStatus(`Autosaving ${changed.length} execution change${changed.length > 1 ? "s" : ""}...`);
      try {
        const saved = await Promise.all(changed.map((record) => saveExecution(normalizedExecutionRecord(record))));
        setRecords((current) => current.map((item) => saved.find((record) => record.stepId === item.stepId) ?? item));
        setAudits(await getExecutionAudit(selectedPlanId) as ExecutionAuditView[]);
        setStatus("Execution autosaved to server");
      } catch {
        dirtyIds.forEach((stepId) => dirtyStepIdsRef.current.add(stepId));
        setStatus("Execution autosave failed; server connection required");
      }
    }, 850);

    return () => window.clearTimeout(timer);
  }, [records, selectedPlanId]);

  const persist = async (record: ExecutionRecord) => {
    dirtyStepIdsRef.current.delete(record.stepId);
    setStatus(`Saving ${record.label}...`);
    try {
      const next = await saveExecution(normalizedExecutionRecord(record));
      setRecords((current) => current.map((item) => (item.stepId === next.stepId ? next : item)));
      setAudits(await getExecutionAudit(next.planId) as ExecutionAuditView[]);
      setStatus(`${record.label} saved`);
    } catch (error) {
      dirtyStepIdsRef.current.add(record.stepId);
      setStatus(error instanceof Error ? error.message : "Execution save failed");
    }
  };

  const switchPlan = async (nextPlanId: string) => {
    const dirtyRecords = records.filter((record) => dirtyStepIdsRef.current.has(record.stepId));
    if (dirtyRecords.length) {
      setStatus("Saving current plan before switching...");
      try {
        await Promise.all(dirtyRecords.map((record) => saveExecution(normalizedExecutionRecord(record))));
        dirtyStepIdsRef.current.clear();
      } catch {
        setStatus("Current changes must be saved before switching plans");
        return;
      }
    }
    setEditor(null);
    setSelectedPlanId(nextPlanId);
  };

  const openAllocationEditor = (record: ExecutionRecord, allocation?: ExecutionAllocationRecord) => {
    setEditor({
      mode: allocation ? "edit" : "add",
      stepId: record.stepId,
      itemId: allocation?.id,
      assetTicker: allocation?.assetTicker ?? "",
      assetName: allocation?.assetName ?? "",
      labelColor: allocation?.labelColor ?? defaultAllocationLabelColor,
      amountText: allocation ? String(allocation.executedAmount) : ""
    });
  };

  const saveAllocationEditor = () => {
    if (!editor?.assetTicker.trim()) return;
    const record = records.find((item) => item.stepId === editor.stepId);
    if (!record) return;
    const current = record.actualAllocations ?? [];
    const item: ExecutionAllocationRecord = {
      id: editor.itemId ?? executionAllocationId(editor.stepId, editor.assetTicker),
      executionId: record.id,
      planId: record.planId,
      stepId: record.stepId,
      assetTicker: editor.assetTicker.trim(),
      assetName: editor.assetName.trim() || editor.assetTicker.trim(),
      labelColor: editor.labelColor,
      executedAmount: editableAmount(editor.amountText)
    };
    const actualAllocations = editor.mode === "edit"
      ? current.map((allocation) => (allocation.id === editor.itemId ? item : allocation))
      : [...current, item];
    const executedAmount = actualAllocations.reduce((sum, allocation) => sum + allocation.executedAmount, 0);
    updateRecord(record.stepId, { actualAllocations, executedAmount, status: executedAmount > 0 ? "executed" : "pending" });
    setEditor(null);
  };

  const deleteActualAllocation = (record: ExecutionRecord, allocationId?: string) => {
    if (!allocationId) return;
    const actualAllocations = (record.actualAllocations ?? []).filter((allocation) => allocation.id !== allocationId);
    const executedAmount = actualAllocations.reduce((sum, allocation) => sum + allocation.executedAmount, 0);
    updateRecord(record.stepId, { actualAllocations, executedAmount, status: executedAmount > 0 ? "executed" : "pending" });
  };

  return (
    <section className="route-page execution-page">
      <div className="page-head compact-head">
        <div>
          <p className="eyebrow">Execution Management</p>
          <h2>Track planned ladders against actual manual execution</h2>
          <span>Review the plan reference, planned assets, actual assets, index level, and deployed amount for every step.</span>
        </div>
        <div className="head-actions execution-head-actions">
          <label className="field compact-select">
            <span>Execution Plan</span>
            <select value={selectedPlanId} onChange={(event) => void switchPlan(event.target.value)}>
              {plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
            </select>
          </label>
          <span className="status-pill warn">{status}</span>
        </div>
      </div>

      {selectedPlan && (
        <div className="execution-reference-strip">
          <ExecMeta label="Reference" value={number(selectedPlan.referenceHigh)} detail={selectedPlan.referenceMode === "intraday-high" ? "Intraday High" : "Close High"} />
          <ExecMeta label="Index" value={selectedPlan.instrumentId.toUpperCase()} detail={selectedPlan.direction === "CP" ? "Buy on decline" : "Sell on advance"} />
          <ExecMeta label="Trigger" value={pct(selectedPlan.direction === "CP" ? -selectedPlan.triggerPct : selectedPlan.triggerPct)} detail={`${selectedPlan.ladderCount + 1} total steps`} />
          <ExecMeta label="Spacing" value={`${selectedPlan.spacingPctPoint.toFixed(1)}%pt`} detail={selectedPlan.spacingBasis === "prior-high" ? "From reference high" : "From previous ladder"} />
        </div>
      )}

      <div className="execution-strip">
        <ExecStat label="Planned Budget" value={money(plannedTotal, currency)} />
        <ExecStat label="Executed" value={money(executedTotal, currency)} />
        <ExecStat label="Budget Used" value={`${budgetUsedPct.toFixed(1)}%`} />
        <ExecStat label="Planned WAI" value={number(plannedWai)} />
        <ExecStat label="Actual WAI" value={number(executedWai)} />
      </div>

      <div className="section execution-ledger-section">
        <div className="section-subhead">
          <span>Execution Ledger</span>
          <span className="subhead-status">Actual asset labels autosave to D1</span>
        </div>
        <div className="execution-step-list">
          {records.map((record, index) => {
            const plannedMovePct = selectedPlan?.referenceHigh ? (record.plannedLevel / selectedPlan.referenceHigh - 1) * 100 : 0;
            const actualMovePct = selectedPlan?.referenceHigh && record.actualLevel ? (record.actualLevel / selectedPlan.referenceHigh - 1) * 100 : null;
            const cumulative = records.slice(0, index + 1).reduce((sum, item) => sum + item.executedAmount, 0);
            const plannedAssets = record.plannedAllocations?.length ? record.plannedAllocations : plannedAllocationsByStep.get(record.stepId) ?? [];
            const actualAssets = record.actualAllocations ?? [];
            const actualAssetTotal = actualAssets.reduce((sum, allocation) => sum + allocation.executedAmount, 0);
            const assetTotalMismatch = actualAssets.length > 0 && Math.abs(actualAssetTotal - record.executedAmount) >= 0.5;

            return (
              <article className="execution-step-row execution-horizontal-row" key={record.stepId}>
                <div className="execution-plan-side">
                  <div className="execution-step-head">
                    <div>
                      <b className={index === 0 ? "red" : "blue"}>{record.label}</b>
                      <span>{number(record.plannedLevel)} <em className={plannedMovePct < 0 ? "red" : "green"}>{pct(plannedMovePct)}</em></span>
                    </div>
                    <b className="execution-plan-budget">{money(record.plannedAmount, currency)}</b>
                  </div>
                  <AllocationLane title="Planned Assets" empty="No planned assets for this step">
                    {plannedAssets.map((allocation) => (
                      <AllocationLabel
                        amount={allocation.plannedAmount}
                        color={allocation.labelColor ?? defaultAllocationLabelColor}
                        currency={currency}
                        key={allocation.id ?? `${allocation.stepId}-${allocation.assetTicker}`}
                        ticker={allocation.assetTicker}
                      />
                    ))}
                  </AllocationLane>
                </div>

                <div className="execution-entry-side">
                  <div className="execution-entry-head">
                    <b>Execution Entry</b>
                    <div className="execution-cumulative"><span>Cumulative</span><b>{money(cumulative, currency)}</b><em>{plannedTotal > 0 ? ((cumulative / plannedTotal) * 100).toFixed(1) : "0.0"}%</em></div>
                  </div>
                  <AllocationLane title="Actual Assets" empty="Add the assets that were actually executed">
                    {actualAssets.map((allocation) => (
                      <AllocationLabel
                        actual
                        amount={allocation.executedAmount}
                        color={allocation.labelColor ?? defaultAllocationLabelColor}
                        currency={currency}
                        key={allocation.id}
                        onDelete={() => deleteActualAllocation(record, allocation.id)}
                        onEdit={() => openAllocationEditor(record, allocation)}
                        ticker={allocation.assetTicker}
                      />
                    ))}
                    <button className="chip-add-button" type="button" onClick={() => openAllocationEditor(record)}><Plus /> Asset</button>
                  </AllocationLane>
                  <div className="execution-input-grid">
                    <label className="field"><span>Actual Index</span><input inputMode="decimal" type="number" value={record.actualLevel ?? ""} onChange={(event) => updateRecord(record.stepId, { actualLevel: event.target.value === "" ? undefined : Number(event.target.value) })} /></label>
                    <label className="field"><span>vs Reference</span><input readOnly value={actualMovePct === null ? "-" : pct(actualMovePct)} /></label>
                    <label className="field"><span>Executed Total</span><input inputMode="decimal" type="number" value={record.executedAmount} onChange={(event) => updateRecord(record.stepId, { executedAmount: Number(event.target.value) || 0 })} /></label>
                    <label className="field"><span>Status</span><select value={record.status} onChange={(event) => updateRecord(record.stepId, { status: event.target.value as ExecutionRecord["status"] })}><option value="pending">Pending</option><option value="executed">Executed</option><option value="skipped">Skipped</option></select></label>
                    <label className="field"><span>Executed At</span><input value={record.executedAt ?? ""} onChange={(event) => updateRecord(record.stepId, { executedAt: event.target.value })} placeholder="YYYY-MM-DD HH:MM" /></label>
                    <label className="field execution-note-field"><span>Note</span><input value={record.note ?? ""} onChange={(event) => updateRecord(record.stepId, { note: event.target.value })} placeholder="Execution note" /></label>
                    <button className="btn primary execution-save-button" type="button" onClick={() => void persist(record)}><Save /> Save Step</button>
                  </div>
                </div>
                {assetTotalMismatch && <div className="execution-mismatch">Actual asset labels total {money(actualAssetTotal, currency)}, while Executed Total is {money(record.executedAmount, currency)}.</div>}
              </article>
            );
          })}
          {!records.length && <div className="ledger-loading">No ladder steps are available for this plan.</div>}
        </div>
      </div>

      <div className="section execution-table-section audit-section">
        <div className="section-subhead">Execution Audit Log</div>
        <div className="table-wrap no-scroll-table">
          <table className="tight-table">
            <thead><tr><th>Time</th><th>Action</th><th>Step</th><th>Note</th></tr></thead>
            <tbody>
              {audits.slice(0, 8).map((audit) => (
                <tr key={audit.id}>
                  <td>{new Date(audit.createdAt ?? audit.created_at ?? "").toLocaleString()}</td>
                  <td>{audit.action}</td>
                  <td>{audit.stepId ?? audit.step_id}</td>
                  <td>{audit.note ?? "Execution record changed"}</td>
                </tr>
              ))}
              {!audits.length && <tr><td colSpan={4}>No audit events recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editor && (
        <ExecutionAllocationModal
          currency={currency}
          editor={editor}
          onChange={setEditor}
          onClose={() => setEditor(null)}
          onSave={saveAllocationEditor}
          step={records.find((record) => record.stepId === editor.stepId)}
        />
      )}
    </section>
  );
}

function AllocationLane({ title, empty, children }: { title: string; empty: string; children: ReactNode }) {
  const childCount = Array.isArray(children) ? children.filter(Boolean).length : children ? 1 : 0;
  return (
    <div className="execution-allocation-lane">
      <span>{title}</span>
      <div className="step-ledger-chips">
        {children}
        {!childCount && <em className="execution-assets-empty">{empty}</em>}
      </div>
    </div>
  );
}

function AllocationLabel({ ticker, amount, currency, color, actual, onEdit, onDelete }: {
  ticker: string;
  amount: number;
  currency: "USD" | "KRW";
  color: string;
  actual?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className={`step-allocation-chip is-compact-view execution-allocation-chip ${actual ? "is-actual" : "is-planned"}`} style={{ borderColor: color, backgroundColor: color }} title={actual ? "Hover to edit or delete actual allocation" : "Planned allocation"}>
      <span className="chip-copy"><b>{ticker}</b><em>{money(amount, currency)}</em></span>
      {actual && <span className="chip-actions"><button type="button" onClick={onEdit}>Edit</button><button type="button" onClick={onDelete}>Delete</button></span>}
    </div>
  );
}

function ExecutionAllocationModal({ currency, editor, onChange, onClose, onSave, step }: {
  currency: "USD" | "KRW";
  editor: ExecutionAllocationEditor;
  onChange: (next: ExecutionAllocationEditor) => void;
  onClose: () => void;
  onSave: () => void;
  step?: ExecutionRecord;
}) {
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Actual execution allocation editor">
      <div className="allocation-modal">
        <div className="modal-head">
          <div><h3>{editor.mode === "edit" ? "Edit Actual Asset" : "Add Actual Asset"}</h3><span>{step?.label ?? "Execution step"} / actual execution</span></div>
          <button className="mini-btn" type="button" onClick={onClose}>Close</button>
        </div>
        <div className="allocation-modal-grid">
          <label className="field"><span>Ticker / Label</span><input autoFocus value={editor.assetTicker} onChange={(event) => onChange({ ...editor, assetTicker: event.target.value })} placeholder="SPY, QQQ, Cash..." /></label>
          <label className="field"><span>Asset Name</span><input value={editor.assetName} onChange={(event) => onChange({ ...editor, assetName: event.target.value })} placeholder="Optional display name" /></label>
          <label className="field"><span>Executed Amount</span><input inputMode="decimal" value={editor.amountText} onChange={(event) => onChange({ ...editor, amountText: event.target.value })} placeholder="0" /></label>
        </div>
        <div className="allocation-color-block">
          <span>Label Color</span>
          <div className="allocation-color-grid">
            {allocationPaletteSwatches.map((color) => <button aria-label={`Use ${color} label color`} className={`allocation-color-swatch ${editor.labelColor === color ? "is-selected" : ""}`} key={color} onClick={() => onChange({ ...editor, labelColor: color })} style={{ backgroundColor: color }} type="button" />)}
          </div>
        </div>
        <div className="allocation-modal-summary"><span>Actual allocation</span><b>{money(editableAmount(editor.amountText), currency)}</b><em>{step?.plannedAmount ? `${((editableAmount(editor.amountText) / step.plannedAmount) * 100).toFixed(1)}% of planned step` : "0.0%"}</em></div>
        <div className="modal-actions"><button className="btn" type="button" onClick={onClose}>Cancel</button><button className="btn primary" type="button" onClick={onSave} disabled={!editor.assetTicker.trim()}>{editor.mode === "edit" ? "Save Changes" : "Add Actual Asset"}</button></div>
      </div>
    </div>
  );
}

function ExecStat({ label, value }: { label: string; value: string }) {
  return <div className="exec-stat"><span>{label}</span><b>{value}</b></div>;
}

function ExecMeta({ label, value, detail }: { label: string; value: string; detail: string }) {
  return <div className="exec-meta"><span>{label}</span><b>{value}</b><em>{detail}</em></div>;
}
